import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import {
  type ProcurementPlanListFilters,
  type TypeFilterValue,
  procurementPlanListFiltersCodec,
} from '~/pages/ProcurementPlanList/models/persistedFilters'
import type { PlanListStatusFilter } from '~/pages/ProcurementPlanList/models/planListStatusFilter'

/**
 * 需要調達計画一覧のフィルタバー保持 key。
 *
 * AC-SC-031 [P0] (`docs/specs/common/shared-components.md`) は「フィルタバーを持つ
 * 全画面において、フィルタの選択状態を localStorage で永続化し、他画面への遷移後・
 * 再訪時に前回の選択状態を復元する」と保存媒体まで規定するため localStorage を使う
 * (タブ生存期間で消える sessionStorage は「再訪時に復元」を満たさない)。
 */
export const PROCUREMENT_PLAN_LIST_FILTERS_STORAGE_KEY =
  'ebalance-procurement-plan-list-filters'

type UsePersistedProcurementPlanFiltersResult = {
  targetDate: Date
  setTargetDate: (value: Date) => void
  areaFilter: string[]
  setAreaFilter: (value: string[]) => void
  typeFilter: TypeFilterValue
  setTypeFilter: (value: TypeFilterValue) => void
  latestOnly: boolean
  setLatestOnly: (value: boolean) => void
  statusFilter: PlanListStatusFilter
  setStatusFilter: (value: PlanListStatusFilter) => void
}

/**
 * フィルタバーの 5 フィルタ (対象日 / エリア / 種別 / 最新のみ / ステータス絞り込み) を localStorage に
 * 保持し、画面遷移 (一覧 → 詳細 → 一覧) や再訪をまたいで復元する hook
 * (issue #6366 / AC-SC-031)。計画期間タブは対象外 (`models/persistedFilters.ts` の
 * docstring 参照)。
 *
 * component-local な `useState` のままだと route 遷移で page が unmount され、
 * 戻った時に全フィルタが初期値へリセットされる。初期値は mount 時に localStorage
 * から復元し、変更のたびに書き戻す。直列化・復元・バリデーションは
 * `models/persistedFilters.ts` の純関数が SoT。
 */
export function usePersistedProcurementPlanFilters(): UsePersistedProcurementPlanFiltersResult {
  // 読み書きのタイミング (初期復元 / 変更時の書き戻し / 未操作の既定値は書かない /
  // Strict Mode 対策) は共通 hook が持つ。 直列化・復元・既定値は
  // `models/persistedFilters.ts` の純関数が SoT。
  const { filters, updateFilters } =
    usePersistedFilters<ProcurementPlanListFilters>({
      storageKey: PROCUREMENT_PLAN_LIST_FILTERS_STORAGE_KEY,
      codec: procurementPlanListFiltersCodec,
    })

  const setFilters = updateFilters

  return {
    targetDate: filters.targetDate,
    setTargetDate: (value) =>
      setFilters((prev) => ({ ...prev, targetDate: value })),
    areaFilter: filters.areaFilter,
    setAreaFilter: (value) =>
      setFilters((prev) => ({ ...prev, areaFilter: value })),
    typeFilter: filters.typeFilter,
    setTypeFilter: (value) =>
      setFilters((prev) => ({ ...prev, typeFilter: value })),
    latestOnly: filters.latestOnly,
    setLatestOnly: (value) =>
      setFilters((prev) => ({ ...prev, latestOnly: value })),
    statusFilter: filters.statusFilter,
    setStatusFilter: (value) =>
      setFilters((prev) => ({ ...prev, statusFilter: value })),
  }
}
